import {
  suggestLibraryItemCode,
  suggestLibraryJobCode,
  suggestLibrarySystemCode,
  WORK_TYPES,
} from '@felix/domain';
import type { TFunction } from 'i18next';
import { foldSearchText } from '../pms/equipment-tree.js';
import type {
  ApplicationContent,
  ApplicationJob,
  LibraryInterval,
  LibraryItem,
  LibraryJob,
  PmsLibrary,
} from './types.js';

/**
 * Kütüphane ekranının saf yardımcıları (M15f; ADR 0063): süzgeç, sıralama ve sihirbazın
 * seçimlerinden uygulama içeriği.
 */

export const PAGE_SIZE = 15;

export type CalendarUnit = 'day' | 'week' | 'month' | 'year';
export const CALENDAR_UNITS: readonly CalendarUnit[] = ['day', 'week', 'month', 'year'];
export const INTERVAL_BASES = ['maker', 'class', 'experience', 'company'] as const;
export const LIBRARY_DEPARTMENTS = [
  'deck',
  'engine',
  'electrical',
  'navigation',
  'safety',
  'accommodation',
  'galley',
  'other',
] as const;
/** Kütüphane iş türleri: gemideki iş türleriyle aynı liste (ISO 14224 dahil; ADR 0133 §4). */
export const LIBRARY_WORK_TYPES = WORK_TYPES;

export const hasInterval = (interval: LibraryInterval) =>
  interval.calendar !== null || interval.runningHours !== null;

/** İş türünden işin sınıfı (Excel'in üç türü; ADR 0063 §2). */
export function jobTypeOf(workType: string): string {
  if (workType === 'Inspection') return 'inspection';
  if (workType === 'Test') return 'test';
  return 'preventive';
}

/* ---------- Elle ekleme: kod önerisi (ADR 0102) ---------- */

/** Yeni sistemin önerilen kodu (canlı ve kaldırılmış kodlardan sonra); öneri yoksa boş. */
export function suggestedSystemCode(library: PmsLibrary): string {
  const codes = [...library.systems.map((s) => s.code), ...(library.retiredCodes?.systems ?? [])];
  return suggestLibrarySystemCode(codes) ?? '';
}

/** Sisteme eklenecek kalemin önerilen kodu: sistem kodu + sıradaki numara. */
export function suggestedItemCode(library: PmsLibrary, systemId: string | null): string {
  const system = library.systems.find((s) => s.id === systemId);
  if (!system) return '';
  const codes = [...library.items.map((i) => i.code), ...(library.retiredCodes?.items ?? [])];
  return suggestLibraryItemCode(system.code, codes) ?? '';
}

/** Kaleme eklenecek işin önerilen kodu: `JOB-` + kalem kodu + sıradaki numara. */
export function suggestedJobCode(library: PmsLibrary, itemId: string | null): string {
  const item = library.items.find((i) => i.id === itemId);
  if (!item) return '';
  const codes = [...library.jobs.map((j) => j.code), ...(library.retiredCodes?.jobs ?? [])];
  return suggestLibraryJobCode(item.code, codes) ?? '';
}

/* ---------- Süzgeç ---------- */

export interface ItemFilter {
  systemId: string | null;
  query: string;
}

export function filterItems(library: PmsLibrary, filter: ItemFilter): LibraryItem[] {
  const q = foldSearchText(filter.query.trim());
  return library.items.filter(
    (item) =>
      (filter.systemId === null || item.systemId === filter.systemId) &&
      (q === '' ||
        [item.code, item.name, item.subSystem ?? '', item.components ?? ''].some((v) =>
          foldSearchText(v).includes(q),
        )),
  );
}

export type IntervalFilter = 'all' | 'set' | 'missing';

export interface JobFilter {
  systemCode: string | null;
  workType: string | null;
  status: string | null;
  interval: IntervalFilter;
  query: string;
}

export const EMPTY_JOB_FILTER: JobFilter = {
  systemCode: null,
  workType: null,
  status: null,
  interval: 'all',
  query: '',
};

export function filterJobs(library: PmsLibrary, filter: JobFilter): LibraryJob[] {
  const q = foldSearchText(filter.query.trim());
  return library.jobs.filter(
    (job) =>
      (filter.systemCode === null || job.systemCode === filter.systemCode) &&
      (filter.workType === null || job.workType === filter.workType) &&
      (filter.status === null || job.status === filter.status) &&
      (filter.interval === 'all' || (filter.interval === 'set') === hasInterval(job.interval)) &&
      (q === '' || [job.code, job.itemCode, job.title].some((v) => foldSearchText(v).includes(q))),
  );
}

export type SortDirection = 'asc' | 'desc';

export function sortRows<T>(
  rows: readonly T[],
  value: (row: T) => string | number,
  dir: SortDirection,
): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const order =
      typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), 'en', { numeric: true });
    return order * sign;
  });
}

export function pageOf<T>(rows: readonly T[], page: number, size = PAGE_SIZE): T[] {
  return rows.slice((page - 1) * size, page * size);
}

export const pagesOf = (count: number, size = PAGE_SIZE) => Math.max(1, Math.ceil(count / size));

/* ---------- Sihirbaz ---------- */

export interface WizardItem {
  selected: boolean;
  code: string;
  name: string;
  tag: string;
  location: string;
  serialNo: string;
  critical: boolean;
  hasCounter: boolean;
}

export interface WizardJob {
  selected: boolean;
  every: number | '';
  unit: CalendarUnit;
  hours: number | '';
  basis: string;
  baselineOn: string;
  baselineHours: number | '';
}

export interface WizardState {
  vesselId: string;
  /** Sistemin gemideki grup kodu (varsayılan sistem kodu). */
  groupCodes: Record<string, string>;
  items: Record<string, WizardItem>;
  jobs: Record<string, WizardJob>;
  note: string;
}

export function wizardItem(item: LibraryItem): WizardItem {
  return {
    selected: false,
    code: item.code,
    name: item.subSystem ?? item.name,
    tag: '',
    location: '',
    serialNo: '',
    critical: item.suggestedCritical,
    hasCounter: false,
  };
}

/** Onaylı ve aralıklı iş varsayılan seçili; aralık kütüphaneden ("aralıkları biz seçeceğiz"). */
export function wizardJob(job: LibraryJob): WizardJob {
  return {
    selected: job.status === 'approved' && hasInterval(job.interval),
    every: job.interval.calendar?.every ?? '',
    unit: job.interval.calendar?.unit ?? 'month',
    hours: job.interval.runningHours ?? '',
    basis: job.intervalBasis ?? '',
    baselineOn: '',
    baselineHours: '',
  };
}

export function emptyWizard(vesselId = ''): WizardState {
  return { vesselId, groupCodes: {}, items: {}, jobs: {}, note: '' };
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());

/** Kalemin notu: bileşen grubu bölünmeden not olur (ADR 0063 §1). */
export function itemNotes(item: LibraryItem): string | null {
  const parts: string[] = [];
  if (item.components) parts.push(`Components: ${item.components}`);
  if (item.notes) parts.push(item.notes);
  return parts.length === 0 ? null : parts.join('\n');
}

/** Seçili kalemlerin seçili işleri (kalem seçilmediyse işi de gitmez). */
export function selectedJobs(library: PmsLibrary, state: WizardState): LibraryJob[] {
  // Dokunulmamış işin seçimi varsayılandan (onaylı ve aralıklı iş seçili gelir)
  return library.jobs.filter(
    (job) =>
      state.items[job.itemId]?.selected === true && (state.jobs[job.id] ?? wizardJob(job)).selected,
  );
}

/** Sihirbazın seçimlerinden uygulama içeriği (ADR 0063 §6). */
export function contentOf(library: PmsLibrary, state: WizardState): ApplicationContent {
  const items = library.items.filter((item) => state.items[item.id]?.selected === true);
  const systemIds = new Set(items.map((item) => item.systemId));
  const systems = library.systems.filter((system) => systemIds.has(system.id));
  const jobs: ApplicationJob[] = selectedJobs(library, state).map((job) => {
    const w = state.jobs[job.id] ?? wizardJob(job);
    const every = w.every === '' ? null : Number(w.every);
    const hours = w.hours === '' ? null : Number(w.hours);
    return {
      libraryId: job.id,
      itemLibraryId: job.itemId,
      code: job.code,
      title: job.title,
      workType: job.workType,
      jobType: job.jobType,
      priority: job.critical ? 'high' : 'medium',
      interval: {
        calendar: every === null ? null : { every, unit: w.unit },
        runningHours: hours,
      },
      intervalBasis: w.basis === '' ? null : w.basis,
      department: job.department,
      manualRef: job.manualRef,
      baselineDoneOn: hours === null || w.baselineOn === '' ? null : w.baselineOn,
      baselineDoneHours: hours === null || w.baselineHours === '' ? null : Number(w.baselineHours),
    };
  });
  return {
    systems: systems.map((system) => ({
      libraryId: system.id,
      code: (state.groupCodes[system.id] ?? '').trim() || system.code,
      name: system.name,
    })),
    items: items.map((item) => {
      const w = state.items[item.id] ?? wizardItem(item);
      return {
        libraryId: item.id,
        systemLibraryId: item.systemId,
        code: w.code.trim() || item.code,
        name: w.name.trim() || item.subSystem || item.name,
        tag: blank(w.tag),
        location: blank(w.location),
        critical: w.critical,
        hasCounter: w.hasCounter,
        notes: itemNotes(item),
        maker: item.maker,
        model: item.model,
        serialNo: blank(w.serialNo),
      };
    }),
    jobs,
  };
}

/** Aralık metni: "3 months / 500 h" (çevrilmiş birimlerle). */
export function intervalLabel(interval: LibraryInterval, t: TFunction): string {
  const parts: string[] = [];
  if (interval.calendar) {
    parts.push(t(`pmsLibrary.units.${interval.calendar.unit}`, { count: interval.calendar.every }));
  }
  if (interval.runningHours !== null) {
    parts.push(t('pmsLibrary.units.hours', { count: interval.runningHours }));
  }
  return parts.join(' / ');
}
